/* eslint-disable @atlassian/tangerine/import/no-parent-imports */

import React, { useState } from 'react';

import { useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Grid } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { useAppContext } from '../../common/ui/app-context';
import {
	LiteAppFilterBar,
	LiteShowAllAppsButton,
} from '../search-page/filters/lite-app-filter-bar';

type LiteHeaderProps = {
	input: React.ReactNode;
	filters: React.ReactNode;
};

const styles = cssMap({
	baseOld: {
		position: 'sticky',
		top: '0',
		backgroundColor: token('elevation.surface'),
		width: '100%',
		backgroundClip: 'padding-box',
		zIndex: 1,
	},
	base: {
		position: 'sticky',
		top: '0',
		backgroundColor: token('elevation.surface'),
		width: '100%',
		backgroundClip: 'padding-box',
		zIndex: 200,
	},
	contentContainer: {
		margin: 'auto',
		maxWidth: '100%',
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		alignItems: 'space-between',
		'@media (min-width: 48rem)': {
			maxWidth: '70rem',
		},
	},
	keyline: {
		borderBottom: `${token('border.width')} solid ${token('color.border')}`,
	},
	gridCollapsed: {
		gridTemplateAreas: '"input ." "appFilterBar toggleBtn"',
		gridTemplateColumns: '1fr auto',
		paddingBottom: token('space.250'),
	},
	gridExpanded: {
		gridTemplateAreas: '"input ." "appFilterBar appFilterBar" "filters toggleBtn"',
		gridTemplateColumns: '1fr auto',
		paddingBottom: token('space.250'),
	},
	inputGridArea: {
		gridArea: 'input',
		display: 'flex',
		alignItems: 'center',
	},
	appFilterBarGridArea: {
		gridArea: 'appFilterBar',
		minWidth: '0px',
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
	},
	appFilterBarScroll: {
		minWidth: '0px',
		flex: '1 1 0',
	},
	toggleBtnGridArea: {
		gridArea: 'toggleBtn',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
		gap: token('space.150'),
	},
	filtersGridArea: {
		gridArea: 'filters',
	},
	paddingTop: {
		paddingTop: token('space.300'),
	},
});

export const LiteHeader = ({
	input,
	filters,
}: LiteHeaderProps): React.JSX.Element => {
	const { isNav4Enabled } = useAppContext();
	const { formatMessage } = useIntl();
	const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);

	return (
		<>
			<Box
				xcss={cx(
					fg('rovo_search_page_header_zindex') ? styles.base : styles.baseOld,
					styles.keyline,
				)}
				testId="search-page-header"
			>
				<Box xcss={styles.contentContainer}>
					<Grid
						xcss={cx(
							isFiltersExpanded ? styles.gridExpanded : styles.gridCollapsed,
							!isNav4Enabled && styles.paddingTop,
						)}
						columnGap="space.200"
						rowGap="space.200"
					>
						{!isNav4Enabled && <Box xcss={styles.inputGridArea}>{input}</Box>}
						<Box xcss={styles.appFilterBarGridArea}>
							<Box xcss={styles.appFilterBarScroll}>
								<LiteAppFilterBar />
							</Box>
							<LiteShowAllAppsButton />
						</Box>
						{isFiltersExpanded && <Box xcss={styles.filtersGridArea}>{filters}</Box>}
						<Box xcss={styles.toggleBtnGridArea}>
							<Button
								appearance="default"
								iconBefore={isFiltersExpanded ? ChevronUpIcon : ChevronDownIcon}
								onClick={() => setIsFiltersExpanded((prev) => !prev)}
							>
								{isFiltersExpanded
									? formatMessage({
											id: 'search_page.header.less_filters',
											defaultMessage: 'Less filters',
											description:
												'Button to collapse the filters section in the search page header',
										})
									: formatMessage({
											id: 'search_page.header.more_filters',
											defaultMessage: 'More filters',
											description:
												'Button to expand the filters section in the search page header',
										})}
							</Button>
						</Box>
					</Grid>
				</Box>
			</Box>
			{isNav4Enabled && <VisuallyHidden>{input}</VisuallyHidden>}
		</>
	);
};
